import { makeAutoObservable, runInAction } from 'mobx'
import { cloneDeep } from 'lodash'
import { logger } from 'shared/lib'
import { RequestCancelAxios } from 'shared/api/lib'
import { type CallHistory } from 'entities/CallHistory/model/CallHistory'
import { ConversationsApi } from 'entities/Conversation'
import { UsersApi } from 'entities/Users'
import { type IOrderType } from 'entities/CallHistory/api/types'
import { getColumns } from 'pages/conversations/pages/calls/ui/CallHistoryTable/ui/getColumns'
import { type CallHistoryStore } from 'pages/conversations/pages/calls/ui/CallHistory'

const defaultSortBy = 'created_at'

export class CallHistoryTableStore {
  private _visibleColumnsIds: string[] = []
  private _hideExpandColumnsIds = ['type']
  private _sortOrder: IOrderType = 'desc'
  private _sortBy = defaultSortBy
  private _cancelRequestGetUsersUiSettings = new RequestCancelAxios()

  constructor(private _callHistoryStore: CallHistoryStore) {
    makeAutoObservable(this)
  }

  get visibleColumnsIds() {
    return this._visibleColumnsIds
  }

  get hideExpandColumnsIds() {
    return this._hideExpandColumnsIds
  }

  get sortOrder() {
    return this._sortOrder
  }

  get sortBy() {
    return this._sortBy
  }

  getSortingParams = () => {
    return {
      field: this._sortBy,
      order: this._sortOrder,
    }
  }

  makeCall = (id: number) => {
    this._callHistoryStore.callStore.connectTwilio(id)
  }

  markAsRead = async (item: CallHistory) => {
    try {
      await ConversationsApi.updateMessagesByIdRead(item.conversation.id, {
        ids: [item.message.id],
      }).then(() => {
        const clonedItem = cloneDeep(item)
        clonedItem.message.unread = false
        this._callHistoryStore.updateItem(clonedItem)
      })
    } catch (e) {
      logger.error(e)
    }
  }

  updateSelectedColumns = async (ids: string[]) => {
    const { data } = await UsersApi.updateUsersUiSettings({
      source_type: 'call-history-table-columns',
      items: ids,
    })

    runInAction(() => {
      if (data?.data?.items) {
        this._visibleColumnsIds = data.data.items
      }
    })
  }

  getSelectedColumns = async () => {
    try {
      this._cancelRequestGetUsersUiSettings.init()

      const { data } = await UsersApi.getUsersUiSettings('call-history-table-columns', {
        cancelToken: this._cancelRequestGetUsersUiSettings.token,
      })

      if (data.data?.items.length) {
        runInAction(() => {
          if (data?.data?.items) {
            this._visibleColumnsIds = data.data.items
          }
        })
      } else {
        this.updateSelectedColumns(
          getColumns([], this._callHistoryStore)
            .filter((column) => !column.isHideByDefault)
            .map((column) => column.field)
        )
      }
    } catch (e) {
      logger.error(e)
    }
  }

  changeSortBy = (field: string) => {
    if (this._sortBy !== field) {
      this._sortBy = field
      this._sortOrder = 'desc'
    }
  }

  toggleSortOrder = () => {
    this._sortOrder = this._sortOrder === 'desc' ? 'asc' : 'desc'
  }

  dispose = () => {
    this._cancelRequestGetUsersUiSettings.reset()
  }
}
